// Audio modal component

import Direction from '../../../../shared/types/enums/direction';
import Event from '@shared/classes/event';
import Focus from '../../../../shared/types/enums/focus';
import Item from './playerLanguageModalItem';
import List from '../../sections/list/list';
import Title from '../../atoms/text/playerLanguageModalTitle';
import Track from '../../atoms/container/playerLanguageModalTrackContainer';
import TranslationManager from '../../../../shared/assets/locale/translationsManager';
import { TrackType } from '@shared/types/interfaces/player';
import { mediaSelector } from '../../../../shared/store/slices/media.slice';
import { modalSelector } from '@shared/store/slices/modal.slice';
import { setTrack } from '@shared/store/thunks/media.thunk';
import { useAppDispatch, useAppSelector } from '../../../../shared/store/hooks';
import { useMemo } from 'react';

/**
 * PlayerLanguageModalAudio component.
 * Displays a list of available audio tracks for selection in the player language modal.
 * Handles audio track selection.
 *
 * @returns {JSX.Element | null} Audio track selection list or null if no audio tracks available
 */
export default function PlayerLanguageModalAudio() {
  const dispatch = useAppDispatch();
  const { audio, tracks } = useAppSelector(mediaSelector);
  const { params } = useAppSelector(modalSelector);

  // Use memo to memoize the event and initial index
  const { event, initialIndex } = useMemo(() => {
    return {
      event: params?.event as Event,
      initialIndex: tracks.audio.findIndex((item) => item.id === audio.id),
    };
  }, [params?.event, tracks.audio, audio.id]);

  // Only render if audio tracks are available
  return tracks.audio.length ? (
    <Track>
      <Title>{TranslationManager.get('MODAL.PLAYER_LANGUAGE.AUDIO')}</Title>
      <List
        direction={Direction.VERTICAL}
        focus={Focus.MODAL_LANGUAGE_AUDIO}
        initialIndex={initialIndex}
      >
        {tracks.audio.map((item, index) => (
          <Item
            action={() =>
              dispatch(setTrack({ event, track: item, type: TrackType.AUDIO }))
            }
            key={index}
            index={index}
            itemFocused={!!audio.language && item.id === audio.id}
            text={TranslationManager.get(
              `MISC.LANGUAGES.TRANSLATED.${item.language}`
            )}
          />
        ))}
      </List>
    </Track>
  ) : null;
}
